import { useState } from 'react';
import { Globe, Loader2 } from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { Setting } from '@/lib/types';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

/**
 * Lo que tusereno.es lee del CRM para comportarse sin desplegar. Vive bajo
 * «Tusereno.es» en el menú, junto a los cupones, y no en la configuración de
 * Leads: es de la web, no de la captación de propietarios.
 */
export default function TuserenoConfigPage() {
  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Globe size={24} className="text-sereno-dark" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Configuración de tusereno.es</h1>
          <p className="text-sm text-sereno-dark/40 mt-0.5">
            Ajustes que la web aplica sola, sin desplegar.
          </p>
        </div>
      </div>
      <TemporadaWeb />
    </div>
  );
}

/** YYYY-MM-DD, que es lo que escribe un <input type="date">. */
const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

const CLAVES = [
  'modo_proxima_temporada',
  'temporada_cerrada_desde',
  'temporada_apertura_texto',
  'banner_descuento',
  'banner_texto',
  'banner_codigo',
];

/** El hueco donde la web pinta el botón que copia el código. */
const HUECO = '{codigo}';
/** Lo que la barra ha dicho siempre; también es el respaldo de la web. */
const BANNER_TEXTO_DEFECTO = `Reserva directo con Sereno · Usa el código ${HUECO} y obtén un 5% de descuento`;
const BANNER_CODIGO_DEFECTO = 'SERENO2026';
const BANNER_TEXTO_MAX = 160;

/** Por qué no se pudo generar el inglés. Mientras tanto la web inglesa enseña el castellano. */
const MOTIVOS_TRADUCCION: Record<string, string> = {
  modelo: 'No se pudo generar la versión inglesa (el modelo no respondió). El castellano se ha guardado; la web inglesa lo enseñará tal cual hasta que vuelvas a guardar.',
  hueco: 'La versión inglesa que propuso el modelo movía o perdía el código, y se ha descartado. El castellano se ha guardado; prueba a guardar de nuevo.',
  larga: 'La versión inglesa no cabía en la barra y se ha descartado. Acorta el mensaje castellano y guarda de nuevo.',
  vacia: 'El modelo devolvió una traducción vacía. El castellano se ha guardado; prueba a guardar de nuevo.',
};

/**
 * La campaña «próxima temporada» de tusereno.es, a modo de interruptor.
 *
 * ENCENDIDA: la web anuncia por todas partes que las fechas del año que se
 * indica aún no están abiertas (pie, home, barra superior, landing) e invita a
 * dejar el correo a quien busca esas fechas en el buscador, los resultados y
 * la ficha. APAGADA: nada de eso; el formulario sigue en el pie, la home y la
 * landing como boletín de novedades, sin hablar de ningún año.
 *
 * Se enciende y se apaga a mano, a propósito: el equipo sabe cuándo empieza
 * la gente a buscar el año siguiente y cuándo se abre el calendario. La web
 * lo lee de `settings/web` con caché de cinco minutos.
 */
function TemporadaWeb() {
  const { data: setting, loading, error, refresh } = useApi<Setting>(() => api.getSetting('web'), []);
  const [fecha, setFecha] = useState('');
  const [texto, setTexto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [cambiandoModo, setCambiandoModo] = useState(false);
  const [cambiandoBanner, setCambiandoBanner] = useState(false);
  // `null` = sin tocar. Con cadena vacía no valdría: el código se puede dejar
  // en blanco a propósito (barra sin botón) y `valor || actual` lo impediría.
  const [bannerTexto, setBannerTexto] = useState<string | null>(null);
  const [bannerCodigo, setBannerCodigo] = useState<string | null>(null);
  const [guardandoBanner, setGuardandoBanner] = useState(false);
  const [bannerGuardado, setBannerGuardado] = useState(false);
  // Qué pasó con la traducción al guardar: `null` = no se ha guardado aún.
  const [traduccionFallida, setTraduccionFallida] = useState<string | null>(null);

  const leer = (id: string) => setting?.values?.find(v => v.id === id)?.label ?? '';
  const encendido = leer('modo_proxima_temporada') === 'on';
  // El banner del código de descuento lleva años puesto: sin ajuste, sigue puesto.
  const banner = leer('banner_descuento') !== 'off';
  const bannerTextoActual = leer('banner_texto') || BANNER_TEXTO_DEFECTO;
  // Lo genera el servidor al guardar el castellano; aquí solo se enseña.
  const bannerTextoIngles = leer('banner_texto_en');
  // Aquí no vale `||`: el ajuste puede estar vacío queriendo, y solo si la
  // clave no existe todavía se cae al código de siempre.
  const bannerCodigoActual =
    setting?.values?.some(v => v.id === 'banner_codigo') ? leer('banner_codigo') : BANNER_CODIGO_DEFECTO;
  const bannerTextoValor = bannerTexto ?? bannerTextoActual;
  const bannerCodigoValor = bannerCodigo ?? bannerCodigoActual;
  const bannerSinCambios =
    bannerTextoValor === bannerTextoActual && bannerCodigoValor === bannerCodigoActual;
  const bannerTextoVacio = bannerTextoValor.trim() === '';
  const fechaActual = leer('temporada_cerrada_desde');
  const textoActual = leer('temporada_apertura_texto');
  const fechaValor = fecha || fechaActual;
  const textoValor = texto || textoActual;
  const fechaValida = FECHA_RE.test(fechaValor);
  const anio = fechaValida ? fechaValor.slice(0, 4) : '—';
  const sinCambios = fechaValor === fechaActual && textoValor === textoActual;

  if (loading) return <LoadingSpinner />;
  if (error || !setting) {
    return (
      <div className="card">
        <p className="text-sm text-sereno-dark/60">
          El ajuste <code>web</code> todavía no existe en la base de datos. Lo crea el servidor al
          arrancar; si acabas de desplegar, recarga en un minuto.
        </p>
      </div>
    );
  }

  /** Escribe las tres claves de una vez, conservando cualquier otra que haya. */
  const escribir = async (cambios: Partial<Record<string, string>>) => {
    const otras = (setting.values ?? []).filter(v => !CLAVES.includes(v.id));
    const actual: Record<string, string> = {
      modo_proxima_temporada: encendido ? 'on' : 'off',
      temporada_cerrada_desde: fechaActual,
      temporada_apertura_texto: textoActual,
      banner_descuento: banner ? 'on' : 'off',
      banner_texto: bannerTextoActual,
      banner_codigo: bannerCodigoActual,
      ...cambios,
    };
    const respuesta = await api.updateSetting('web', {
      values: [...otras, ...CLAVES.map(id => ({ id, label: actual[id] ?? '' }))],
    });
    refresh();
    return respuesta as { traduccion?: { ok: boolean; motivo?: string } } | undefined;
  };

  const alternarModo = async () => {
    setCambiandoModo(true);
    try {
      await escribir({ modo_proxima_temporada: encendido ? 'off' : 'on' });
    } finally {
      setCambiandoModo(false);
    }
  };

  const alternarBanner = async () => {
    setCambiandoBanner(true);
    try {
      await escribir({ banner_descuento: banner ? 'off' : 'on' });
    } finally {
      setCambiandoBanner(false);
    }
  };

  const guardarBanner = async () => {
    if (bannerTextoVacio) return;
    setGuardandoBanner(true);
    setBannerGuardado(false);
    setTraduccionFallida(null);
    try {
      const respuesta = await escribir({
        banner_texto: bannerTextoValor.trim().slice(0, BANNER_TEXTO_MAX),
        banner_codigo: bannerCodigoValor.trim(),
      });
      setBannerGuardado(true);
      if (respuesta?.traduccion && !respuesta.traduccion.ok) {
        setTraduccionFallida(MOTIVOS_TRADUCCION[respuesta.traduccion.motivo ?? ''] ?? MOTIVOS_TRADUCCION.modelo);
      }
      setBannerTexto(null);
      setBannerCodigo(null);
    } finally {
      setGuardandoBanner(false);
    }
  };

  const guardar = async () => {
    if (!fechaValida) return;
    setGuardando(true);
    setGuardado(false);
    try {
      await escribir({
        temporada_cerrada_desde: fechaValor,
        temporada_apertura_texto: textoValor.trim() || 'a finales de año',
      });
      setGuardado(true);
      setFecha('');
      setTexto('');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-2">
          Campaña «próxima temporada»
        </h3>
        <p className="text-sm text-sereno-dark/60 mb-4">
          Encendida, la web anuncia que las fechas de {anio} aún no están abiertas y pide el correo a
          quien las busca: en el pie, la home, la barra superior, la landing <code>/proxima-temporada</code>,
          el buscador, los resultados y la ficha. Apagada, no habla de ningún año: el formulario se queda
          como boletín de novedades. Encendedla cuando la gente empiece a buscar el año siguiente y
          apagadla el día que abráis el calendario. El cambio tarda hasta cinco minutos en verse.
        </p>
        <div className="flex items-center gap-4">
          <button
            type="button"
            role="switch"
            aria-checked={encendido}
            aria-label="Modo próxima temporada"
            onClick={alternarModo}
            disabled={cambiandoModo}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
              encendido ? 'bg-green-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                encendido ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <div>
            <div className="text-sm font-semibold text-sereno-dark">
              {encendido ? `Encendida: la web anuncia la temporada ${anio}` : 'Apagada: la web no habla de la próxima temporada'}
            </div>
            <div className="text-xs text-sereno-dark/50">
              {encendido
                ? 'Apagadla el día que abráis el calendario.'
                : 'Antes de encenderla, comprobad abajo el año y el texto de apertura.'}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-2">
          Barra superior
        </h3>
        <p className="text-sm text-sereno-dark/60 mb-4">
          La barra superior de tusereno.es, con el mensaje que escribáis aquí. Con la campaña de
          temporada encendida, los dos mensajes se alternan; si solo hay uno, se queda fijo; sin
          ninguno, no hay barra. El cambio tarda hasta cinco minutos en verse.
        </p>
        <div className="flex items-center gap-4">
          <button
            type="button"
            role="switch"
            aria-checked={banner}
            aria-label="Banner del código de descuento"
            onClick={alternarBanner}
            disabled={cambiandoBanner}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
              banner ? 'bg-green-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                banner ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <div className="text-sm font-semibold text-sereno-dark">
            {banner ? 'Puesta: la web enseña la barra' : 'Quitada: la web no enseña la barra'}
          </div>
        </div>

        <div className={`mt-5 border-t border-gray-100 pt-5 ${banner ? '' : 'opacity-50'}`}>
          <label className="label" htmlFor="banner-texto">Qué dice la barra</label>
          <textarea
            id="banner-texto"
            className="input w-full min-h-[72px] py-2 leading-relaxed"
            maxLength={BANNER_TEXTO_MAX}
            value={bannerTextoValor}
            onChange={e => { setBannerTexto(e.target.value); setBannerGuardado(false); }}
          />
          <div className="flex flex-wrap items-start justify-between gap-2 mt-1">
            <p className="text-xs text-sereno-dark/50 max-w-2xl">
              Escribe lo que quieras, en castellano. Donde pongas <code>{HUECO}</code> saldrá el
              código como botón para copiarlo; si lo quitas, la barra es solo texto. La versión
              inglesa se genera sola al guardar, con el código en el mismo sitio.
            </p>
            <span className="text-xs text-sereno-dark/40 tabular-nums">
              {bannerTextoValor.length}/{BANNER_TEXTO_MAX}
            </span>
          </div>

          <div className="flex flex-wrap items-end gap-3 mt-4">
            <div>
              <label className="label" htmlFor="banner-codigo">Código (opcional)</label>
              <input
                id="banner-codigo"
                className="input h-11 w-52 font-mono"
                type="text"
                maxLength={24}
                placeholder="Sin código"
                value={bannerCodigoValor}
                onChange={e => { setBannerCodigo(e.target.value); setBannerGuardado(false); }}
              />
            </div>
            <button
              onClick={guardarBanner}
              disabled={guardandoBanner || bannerSinCambios || bannerTextoVacio}
              className="btn-primary h-11 text-sm flex items-center gap-2"
            >
              {guardandoBanner && <Loader2 size={16} className="animate-spin" />}
              Guardar
            </button>
            {bannerGuardado && <span className="text-sm text-green-700 pb-3">Guardado.</span>}
            {bannerTextoVacio && <span className="text-sm text-red-600 pb-3">Escribe algo, o quita la barra con el interruptor.</span>}
          </div>

          {/* Cómo se leerá: con texto libre y un hueco, verlo antes ahorra sustos. */}
          <div className="mt-4">
            <div className="text-xs text-sereno-dark/50 mb-1.5">Así se verá:</div>
            <div className="rounded-lg bg-sereno-dark px-4 py-2.5 text-center">
              <span className="text-sm text-white">
                {bannerCodigoValor.trim() && bannerTextoValor.includes(HUECO)
                  ? bannerTextoValor.split(HUECO).reduce<React.ReactNode[]>((acc, parte, i) => {
                      if (i === 0) return [parte];
                      // Solo el primer hueco es botón, como en la web.
                      return i === 1
                        ? [...acc, <code key="c" className="mx-1 px-1.5 rounded bg-white/15 tracking-wider">{bannerCodigoValor.trim()}</code>, parte]
                        : [...acc, HUECO, parte];
                    }, [])
                  : bannerTextoValor.split(HUECO).join('').replace(/\s{2,}/g, ' ').trim()}
              </span>
            </div>
          </div>

          {/* La web inglesa: lo que ha generado el modelo con el último guardado. */}
          <div className="mt-3">
            <div className="text-xs text-sereno-dark/50 mb-1.5">Así se verá en inglés (se genera al guardar):</div>
            {bannerTextoIngles ? (
              <div className="rounded-lg bg-sereno-dark px-4 py-2.5 text-center">
                <span className="text-sm text-white">
                  {bannerCodigoActual.trim() && bannerTextoIngles.includes(HUECO)
                    ? bannerTextoIngles.split(HUECO).reduce<React.ReactNode[]>((acc, parte, i) =>
                        i === 0 ? [parte]
                        : i === 1 ? [...acc, <code key="c" className="mx-1 px-1.5 rounded bg-white/15 tracking-wider">{bannerCodigoActual.trim()}</code>, parte]
                        : [...acc, HUECO, parte], [])
                    : bannerTextoIngles.split(HUECO).join('').replace(/\s{2,}/g, ' ').trim()}
                </span>
              </div>
            ) : (
              <p className="text-xs text-sereno-dark/50">
                Todavía no hay versión inglesa: la web inglesa enseña el texto castellano. Guarda el
                mensaje para generarla.
              </p>
            )}
            {traduccionFallida && (
              <p className="text-xs text-amber-700 mt-1.5">{traduccionFallida}</p>
            )}
            {bannerTexto !== null && bannerTexto.trim() !== bannerTextoActual && (
              <p className="text-xs text-sereno-dark/40 mt-1.5">
                Has cambiado el castellano: al guardar se generará el inglés de nuevo.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-2">
          Qué temporada se anuncia
        </h3>
        <p className="text-sm text-sereno-dark/60 mb-4">
          Desde esta fecha (incluida) la web considera que las fechas pertenecen a la temporada aún no
          abierta; el año de esa fecha es el que sale en los textos. Lo normal es el 1 de enero del
          año que viene. Solo se aplica con la campaña encendida.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label" htmlFor="temporada-cerrada-desde">Cerrada desde</label>
            <input
              id="temporada-cerrada-desde"
              className="input h-11 w-44"
              type="date"
              value={fechaValor}
              onChange={e => { setFecha(e.target.value); setGuardado(false); }}
            />
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="label" htmlFor="temporada-apertura-texto">Cuándo se abre (texto que ve la persona)</label>
            <input
              id="temporada-apertura-texto"
              className="input h-11 w-full"
              type="text"
              maxLength={60}
              placeholder="a finales de año"
              value={textoValor}
              onChange={e => { setTexto(e.target.value); setGuardado(false); }}
            />
          </div>
          <button
            onClick={guardar}
            disabled={guardando || !fechaValida || sinCambios}
            className="btn-primary h-11 text-sm flex items-center gap-2"
          >
            {guardando && <Loader2 size={16} className="animate-spin" />}
            Guardar
          </button>
          {guardado && <span className="text-sm text-green-700 pb-3">Guardado.</span>}
          {!fechaValida && <span className="text-sm text-red-600 pb-3">Hace falta una fecha.</span>}
        </div>
      </div>
    </div>
  );
}
